import { Button } from '@mui/material';
import { useDeleteOperatorsScheduleMutation } from 'api/queries/operators';
import { useUsersCurrentAccessesQuery } from 'api/queries/users';
import VehicleOfflineForm from 'components/forms/FleetFormFactory/VehicleOfflineForm';
import { OfflineScheduleItemSchemaType } from 'components/forms/types';
import { ConfirmModal, ModalBase, UserPermission } from 'remote/global';
import { useState } from 'react';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

type ModalProps = {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>; // Used to change the open state
  onClose: () => void; // Used to clear the open and component state
  isEditMode?: boolean;
  defaultValues?: Partial<OfflineScheduleItemSchemaType>;
};

function VehicleOfflineModal(props: ModalProps) {
  const { open, setOpen, defaultValues, onClose, isEditMode } = props;
  const { data: userInfo } = useUsersCurrentAccessesQuery();
  const { permissions } = userInfo ?? {};
  const hasDeleteSchedulePermissions =
    ![UserPermission.NO_ACCESS, UserPermission.RESTRICTED].includes(
      permissions?.DRIVER_SCHEDULE as UserPermission
    ) &&
    ![UserPermission.NO_ACCESS, UserPermission.RESTRICTED].includes(
      permissions?.VEHICLE_SCHEDULE as UserPermission
    );
  const [isConfirmDeleteModalOpen, setIsConfirmDeleteModalOpen] = useState(false);
  const { t } = useTranslation();
  const removeScheduleMutation = useDeleteOperatorsScheduleMutation();

  const isRemoveAllowed = isEditMode && hasDeleteSchedulePermissions;

  const handleCloseModal = () => {
    setIsConfirmDeleteModalOpen(false);
    setOpen(false);
    onClose();
  };

  const handleDeleteScheduleItem = async () => {
    try {
      await removeScheduleMutation.mutateAsync({ id: String(defaultValues?.resourceId) });
      handleCloseModal();
    } catch (err) {
      toast.error(t('Toast.failure'));
    }
  };

  return (
    <>
      <ModalBase
        open={open}
        title={
          isEditMode
            ? t('modals.offlineEventType.edit.title').toString()
            : t('modals.offlineEventType.create.title').toString()
        }
        setOpen={setOpen}
        onClose={handleCloseModal}
        {...(isRemoveAllowed && {
          header: {
            children: (
              <Button variant='outlined' onClick={() => setIsConfirmDeleteModalOpen(true)}>
                {t('delete')}
              </Button>
            ),
          },
        })}
      >
        <VehicleOfflineForm {...props} isEditMode={isEditMode} />
      </ModalBase>
      <ConfirmModal
        open={isConfirmDeleteModalOpen}
        onConfirm={handleDeleteScheduleItem}
        setOpen={setIsConfirmDeleteModalOpen}
        onClose={() => setIsConfirmDeleteModalOpen(false)}
        title={t('modals.confirm.title').toString()}
      />
    </>
  );
}

export default VehicleOfflineModal;
